import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Row,
  Select,
  Typography,
  notification,
} from "antd";
import { useEffect, useState } from "react";
import { ProfilePictureUpload } from "../profile-picture-upload/ProfilePictureUpload";
import "./ProfileSettingsForm.scss";
import { PhoneMaskedInput } from "shared/components";
import { useBoundStore } from "store";
import { useUpdateTenantUserMutation, useTenantUser } from "users/queries";
import { useRequestEmailChangeMutation } from "auth/queries";

export const ProfileSettingsForm = () => {
  const [form] = Form.useForm();
  const user = useBoundStore((state) => state.user);
  const tenant = useBoundStore((state) => state.tenant);
  const isAxiosInterceptorsReady = useBoundStore((state) => state.isAxiosInterceptorsReady);
  const { mutateAsync: updateUser, isPending, error } = useUpdateTenantUserMutation();
  const emailChangeMutation = useRequestEmailChangeMutation();
  const [api, contextHolder] = notification.useNotification();
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");

  const { data: tenantUser } = useTenantUser(user?.auth0UserId, tenant?.id, {
    enabled: !!user?.auth0UserId && !!tenant?.id && isAxiosInterceptorsReady,
  });
  const appUser = (tenantUser as any)?.user;

  useEffect(() => {
    if (!appUser) return;

    const digits = ((appUser.phoneNumber ?? "") as string).replace(/\D/g, "").slice(-10);
    form.setFieldsValue({
      firstName: appUser.firstName ?? "",
      lastName: appUser.lastName ?? "",
      phoneNumber: digits ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}` : "",
      company: appUser.company ?? "",
      service: appUser.service ?? "",
    });
  }, [appUser, form, user?.email]);

  const onFinishHandler = async (values: any) => {
    const digits = (values?.phoneNumber ?? "").replace(/\D/g, "");
    const phoneNumber = digits ? `+1${digits}` : "";

    await updateUser(
      {
        firstName: values?.firstName?.trim(),
        lastName: values?.lastName?.trim(),
        phoneNumber,
        company: values?.company?.trim(),
        service: values?.service ?? "",
      },
      {
        onSuccess: () => {
          api.success({
            duration: 3,
            message: "Update profile information complete",
            description: "Your profile information has been updated.",
          });
        },
      }
    );
  };

  return (
    <div className="profile-settings-form-content">
      {contextHolder}
      <Typography.Title level={5}>Your profile picture</Typography.Title>
      <ProfilePictureUpload />
      <Typography.Title level={5} style={{ marginTop: 24 }}>Your profile information</Typography.Title>
      {error && (
        <Alert
          data-testid='profile-settings-error'
          className='profile-settings-error'
          message='Error'
          description={error?.message}
          type='error'
          showIcon
        />
      )}
      <Form
        form={form}
        name='profile-settings-form'
        data-testid='profile-settings-form'
        layout='vertical'
        onFinish={onFinishHandler}
        requiredMark={false}
      >
        <Row gutter={{ xs: 4, sm: 8, md: 16, lg: 20 }}>
          <Col span={12}>
            <Form.Item
              name='firstName'
              data-testid='firstName'
              label='First Name'
              rules={[
                { required: true, message: "Please enter your first name." },
                { type: "string", max: 50, message: "Your first name must be 50 characters or less." },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='lastName'
              data-testid='lastName'
              label='Last Name'
              rules={[
                { required: true, message: "Please enter your last name." },
                { type: "string", max: 50, message: "Your last name must be 50 characters or less." },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ xs: 4, sm: 8, md: 16, lg: 20 }}>
          <Col span={12}>
            <Form.Item label='Email' data-testid='email'>
              <Typography.Text>{appUser?.email ?? user?.email ?? ""}</Typography.Text>
              {!isEditingEmail ? (
                <div style={{ marginTop: 8 }}>
                  <Button type='link' size='small' style={{ padding: 0 }} onClick={() => setIsEditingEmail(true)}>
                    Change email
                  </Button>
                </div>
              ) : (
                <div style={{ marginTop: 8 }}>
                  <Input
                    placeholder='New email address'
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    style={{ marginBottom: 8 }}
                  />
                  <Button
                    type='primary'
                    size='small'
                    loading={emailChangeMutation.isPending}
                    disabled={!newEmail.trim()}
                    onClick={async () => {
                      try {
                        await emailChangeMutation.mutateAsync(newEmail.trim());
                        api.success({
                          duration: 5,
                          message: "Verification email sent",
                          description: `A verification email has been sent to ${newEmail}. Your email will update after verification.`,
                        });
                        setIsEditingEmail(false);
                        setNewEmail("");
                      } catch (err: any) {
                        api.error({
                          message: "Error",
                          description: err?.response?.data?.message ?? "Failed to update email.",
                        });
                      }
                    }}
                  >
                    Send Verification
                  </Button>
                  <Button
                    type='text'
                    size='small'
                    style={{ marginLeft: 8 }}
                    onClick={() => { setIsEditingEmail(false); setNewEmail(""); }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='phoneNumber'
              data-testid='phoneNumber'
              label='Phone'
              rules={[
                {
                  validator: (_: any, value: string) => {
                    if (!value) return Promise.resolve();
                    const digits = value.replace(/\D/g, "");
                    if (digits.length === 10) return Promise.resolve();
                    return Promise.reject("Please enter a valid US phone number (e.g., (123) 456-7890).");
                  },
                },
              ]}
            >
              <PhoneMaskedInput />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ xs: 4, sm: 8, md: 16, lg: 20 }}>
          <Col span={12}>
            <Form.Item name='company' data-testid='company' label='Your Company'>
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label='Your Service'
              name='service'
              data-testid='service'
              className='profile-settings-service'
            >
              <Select
                options={[
                  { value: "", label: "Select..." },
                  { value: "hvac", label: "HVAC" },
                  { value: "lighting", label: "Lighting" },
                  { value: "refrigeration", label: "Refrigeration" },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
        <Button
          type='primary'
          size='large'
          htmlType='submit'
          loading={isPending}
          className='profile-settings-button'
          data-testid='profile-settings-button'
        >
          Update Profile
        </Button>
      </Form>
    </div>
  );
};
